import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, useGameState, fmtClock, pointsIfCorrect } from "@/lib/api";
import { LogoLockup, LogoMark, BrainTapLockup, BrainTapLogo } from "@/components/Logo";
import Avatar from "@/components/Avatar";
import { QRCodeSVG } from "qrcode.react";
import { Brain, Flame, Radio, Users, UtensilsCrossed, Globe2, MapPin, Trophy } from "lucide-react";
import { Watermark } from "@/components/Watermark";
import { BadgeRow } from "@/components/Badges";

const BREAK_TOTAL = 600;

const CATEGORY_LABELS = {
  general: "General Knowledge",
  sports: "Sports Trivia",
  music: "Music Trivia",
  math: "Math & Brain Teasers",
  science: "Science Trivia",
  history: "History Trivia",
  pop_culture: "Pop Culture",
  ultimate: "Ultimate General Trivia",
};

function BreakShow({ code, venue, winner, taunt, winnerBadges, secondsLeft, intermissionCode, checkedInCount, adsOnTv }) {
  const [boards, setBoards] = useState(null);
  const [areaIdx, setAreaIdx] = useState(0);
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/game/${code}/boards`);
        if (alive) setBoards(data);
      } catch (e) { /* retried on interval */ }
    };
    load();
    const iv = setInterval(load, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [code]);

  const gallery = (venue.gallery || []).map((g) => ({
    image_url: g.image_url || g,
    caption: g.caption || "",
    caption_font: g.caption_font || "baloo",
    caption_pos: g.caption_pos || "bottom",
  }));
  const specialPhotos = (venue.specials || []).filter((s) => s.image_url).map((s) => ({
    image_url: s.image_url, caption: s.title, caption_font: "baloo", caption_pos: "bottom",
  }));
  const photos = [...specialPhotos, ...gallery];

  useEffect(() => {
    const iv = setInterval(() => setAreaIdx((i) => i + 1), 20000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    if (photos.length < 2) return undefined;
    const iv = setInterval(() => setPhotoIdx((i) => i + 1), 5000);
    return () => clearInterval(iv);
  }, [photos.length]);

  const metro = (((boards && boards.metro) || venue.region || "Your area").trim()) || "Your area";
  const areas = boards
    ? [
        { label: `Tonight at ${venue.name}`, rows: (boards.venue || []).map((r) => ({ label: r.name, value: r.score !== undefined ? r.score : r.total_points, mine: false, sub: r.venue_name, streak: r.streak })) },
        { label: `${metro} bar league`, rows: (boards.city || []).map((r) => ({ label: r.venue_name, value: r.avg_score, mine: r.venue_code === venue.code, sub: `${r.players} player${r.players === 1 ? "" : "s"} this week` })) },
        { label: "Nationwide scores", rows: (boards.nation || []).map((r) => ({ label: r.name, value: r.score !== undefined ? r.score : r.total_points, mine: false, sub: r.venue_name, streak: r.streak })) },
      ]
    : [];
  if (!boards) return null;
  const area = areas.length ? areas[areaIdx % areas.length] : null;
  const photo = photos.length ? photos[photoIdx % photos.length] : null;
  const prevPhoto = photos.length > 1 ? photos[(photoIdx - 1 + photos.length) % photos.length] : null;
  const fontFor = (f) => (f === "bricolage" ? "'Bricolage Grotesque', sans-serif" : f === "mono" ? "'Space Mono', monospace" : f === "serif" ? "Georgia, 'Times New Roman', serif" : "'Baloo 2', sans-serif");

  return (
    <div className="flex w-full flex-1 flex-col" data-testid="tv-break-show">
      <div className="absolute left-1/2 top-3 z-[6] -translate-x-1/2 rounded-full border border-amber/40 bg-navy-900/85 px-6 py-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-amber-soft backdrop-blur" data-testid="tv-break-clock">
        Next game in {fmtClock(secondsLeft)}
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="flex w-[36%] flex-col overflow-hidden border-r border-ice/10 bg-navy-900/90 px-7 pt-6 pb-28" data-testid="tv-places-panel">
          {winner && (
            <div className="mb-4 flex items-center gap-3 rounded-2xl border border-amber/40 bg-amber/10 px-4 py-3" data-testid="tv-break-winner-strip">
              <motion.img
                src="/brand/braintap-mascot.png"
                alt="BrainTap mascot"
                className="w-12 shrink-0"
                animate={{ y: [0, -8, 0], rotate: [-3, 3, -3] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                data-testid="tv-winner-mascot"
              />
              <div className="min-w-0">
                <div className="truncate font-display font-extrabold uppercase text-amber-soft" data-testid="tv-break-winner-name">{winner.name} won last game</div>
                {taunt && <div className="truncate text-xs text-mut">"{taunt}"</div>}
              </div>
            </div>
          )}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div key={areaIdx} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.45 }} className="absolute inset-0">
                {area ? (
                  area.rows.length ? (
                  <>
                    <div className="mb-4 flex items-center gap-3 font-mono text-xs font-bold uppercase tracking-[0.3em] text-amber" data-testid="tv-board-label">
                      <Trophy size={14} /> {area.label}
                    </div>
                    <div className={`space-y-2 pr-2 ${area.rows.length > 3 ? "tk-scroll-y" : ""}`}>
                      {(area.rows.length > 3 ? [...area.rows.slice(0, 6), ...area.rows.slice(0, 6)] : area.rows.slice(0, 6)).map((r, i) => (
                        <div key={i} className={`flex items-center gap-4 rounded-2xl border px-5 py-3.5 text-left ${r.mine ? "border-amber/60 bg-gradient-to-r from-amber/25 to-amber/5" : "border-ice/15 bg-navy-800/70"}`} data-testid={`tv-place-row-${i}`}>
                          <span className="font-mono-num w-7 text-lg font-bold text-mut">{(i % area.rows.length) + 1}</span>
                          {(i % area.rows.length) === 0 ? <Brain size={24} className="shrink-0 text-amber" fill="#f5b335" /> : <Trophy size={20} className="shrink-0 text-mut" />}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 truncate font-display text-lg font-bold text-cream">
                              {r.label}
                              {r.streak >= 2 && <span className="flex items-center gap-1 rounded-full bg-coral/15 px-2 py-0.5 font-mono text-[10px] font-bold text-coral"><Flame size={11} />×{r.streak}</span>}
                            </div>
                            {r.sub && <div className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-mut">{r.sub}</div>}
                          </div>
                          <span className="font-mono-num text-xl font-bold text-mint">{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                  ) : (
                    <div className="rounded-2xl border border-ice/15 bg-navy-800/60 px-6 py-8 text-center text-mut" data-testid="tv-board-empty">No {area.label} scores yet — the first games of the night write history.</div>
                  )
                ) : (
                  <div className="rounded-2xl border border-ice/15 bg-navy-800/60 px-6 py-8 text-center text-mut">Loading the boards…</div>
                )}
              </motion.div>
            </AnimatePresence>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-navy-900 to-transparent" />
          </div>
          {(intermissionCode || adsOnTv) && (
            <div className="mt-3 space-y-3">
              {intermissionCode && (
                <div className="rounded-2xl border-2 border-amber/60 bg-gradient-to-br from-amber/20 to-grape/10 px-5 py-4 text-center" data-testid="tv-checkin-banner">
                  <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber-soft">Intermission check-in</div>
                  <div className="font-mono-num my-1.5 text-6xl font-extrabold tracking-[0.18em] text-amber text-glow-amber" data-testid="tv-checkin-code">{intermissionCode}</div>
                  <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-cream">
                    Type this on your phone to stay in the game{checkedInCount > 0 ? ` · ${checkedInCount} checked in` : ""}
                  </div>
                </div>
              )}
              {adsOnTv && (
                <a href="https://braintap.bar" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-3 rounded-2xl border border-grape/50 bg-navy-800/80 px-5 py-3 transition-transform hover:scale-[1.01]" data-testid="tv-play-at-home-ad">
                  <img src="/brand/braintap-mascot.png" alt="" className="h-9 w-9 shrink-0 object-contain" />
                  <span className="font-display text-base font-extrabold uppercase leading-tight text-cream">
                    Want to play at home? Go to <span className="text-amber">braintap.bar</span>
                  </span>
                </a>
              )}
            </div>
          )}
        </div>
        <div className="relative flex-1" data-testid="tv-photo-stage">
          {photo ? (
            <>
              {prevPhoto && <img src={prevPhoto.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
              <img key={photoIdx} src={photo.image_url} alt={photo.caption || "Venue special"} className="tk-fade-slow absolute inset-0 h-full w-full object-cover" data-testid="tv-break-photo" />
              {photo.caption && (
                <div className={`absolute left-10 right-10 z-[1] ${photo.caption_pos === "top" ? "top-12" : photo.caption_pos === "center" ? "top-1/2 -translate-y-1/2 text-center" : "bottom-24 text-center"}`} data-testid="tv-photo-caption-box">
                  <span
                    className="inline-block rounded-2xl border border-amber/40 bg-navy-900/85 px-8 py-4 text-4xl font-extrabold leading-tight text-cream shadow-[0_18px_60px_rgba(0,0,0,0.55)] backdrop-blur"
                    style={{ fontFamily: fontFor(photo.caption_font) }}
                    data-testid="tv-photo-caption"
                  >
                    {photo.caption}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-navy-800 to-navy-900">
              <BrainTapLockup className="float-slow w-96" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MascotTakeover({ onDone }) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center"
      initial={{ scale: 0.08, opacity: 0 }}
      animate={{ scale: [0.08, 1.65, 1.65, 0.04], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 5.6, times: [0, 0.42, 0.72, 1], ease: ["backOut", "linear", "anticipate"] }}
      onAnimationComplete={onDone}
      data-testid="mascot-takeover"
    >
      <video
        src="/brand/mascot-takeover-v3.webm"
        autoPlay
        muted
        playsInline
        onEnded={onDone}
        className="w-[82vw] max-w-[880px]"
        data-testid="mascot-takeover-video"
      />
    </motion.div>
  );
}

export default function TV() {
  const { code } = useParams();
  const roomCode = code || "847291";
  const { state, secondsLeft } = useGameState(roomCode, false);
  const [takeoverDone, setTakeoverDone] = useState(false);

  useEffect(() => {
    document.title = `BrainTap — Live`;
  }, []);

  useEffect(() => {
    if (state && state.phase !== "break") setTakeoverDone(false);
  }, [state && state.phase]);

  if (!state) {
    return <div className="flex h-screen items-center justify-center bg-navy-900 font-mono text-sm uppercase tracking-[0.3em] text-mut">Warming up the big screen…</div>;
  }

  if (state.notFound) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-navy-900 text-center" data-testid="tv-not-found">
        <LogoLockup markClass="h-12" />
        <h1 className="font-display text-4xl font-extrabold uppercase text-cream">Room not found</h1>
        <p className="text-mut">No trivia room runs on code "{roomCode}".</p>
        <Link to="/join" className="rounded-full bg-amber px-8 py-3.5 font-display font-extrabold uppercase text-navy-900">Go to join</Link>
      </div>
    );
  }

  const { venue, phase } = state;
  const qTime = state.question_time || 10;
  const frac = Math.max(0, Math.min(1, secondsLeft / qTime));
  const winnerMoment = false;

  return (
    <div
      className="relative flex h-screen flex-col overflow-hidden text-cream"
      style={{
        background:
          "radial-gradient(circle at 20% 10%, rgba(255,180,60,0.15), transparent 50%), radial-gradient(circle at 80% 15%, rgba(196,77,240,0.20), transparent 55%), radial-gradient(circle at 50% 105%, rgba(255,77,157,0.14), transparent 55%), linear-gradient(160deg,#381a4d 0%,#261033 55%,#190a20 100%)",
      }}
      data-testid="tv-page"
    >
      <Watermark />
      <img src="/brand/braintap-logo-full.png" alt="braintap.bar" className="pointer-events-none absolute right-3 top-3 z-30 h-16 w-auto drop-shadow-[0_4px_18px_rgba(0,0,0,0.5)] sm:right-4 sm:h-28" data-testid="tv-braintap-logo" />
      {state.phase === "break" && !takeoverDone && <MascotTakeover onDone={() => setTakeoverDone(true)} />}
      {state.paused && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-navy-900/70 backdrop-blur-sm" data-testid="tv-paused-overlay">
          <div className="font-display text-6xl font-extrabold uppercase tracking-[0.25em] text-amber text-glow-amber">Paused</div>
        </div>
      )}

      {phase !== "break" && (
        <header className="relative z-[1] grid grid-cols-[1fr_auto_1fr] items-start px-5 pt-2 lg:px-8">
          <div className="flex flex-col items-start gap-1.5">
            <div className="flex items-center gap-2 rounded-full bg-navy-900/70 px-3.5 py-1.5 backdrop-blur">
              <span className="live-dot h-2.5 w-2.5 rounded-full bg-coral shadow-[0_0_14px_#ff5a82]" />
              <span className="font-display text-sm font-extrabold tracking-[0.2em] text-coral">LIVE</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-navy-900/60 px-3 py-0.5 font-mono text-[10px] tracking-[0.15em] text-mut backdrop-blur">
              <Users size={10} />
              <span data-testid="tv-players-online" className="text-mint">{state.players_online}</span> ONLINE
            </div>
          </div>
          <div className="flex items-center justify-center" data-testid="tv-venue-brand">
            {venue.logo_url ? (
              <img src={venue.logo_url} alt={venue.name} className="h-44 lg:h-60 max-w-[460px] lg:max-w-[680px] object-contain drop-shadow-[0_6px_22px_rgba(0,0,0,0.55)]" />
            ) : (
              <div data-testid="tv-venue-name" className="font-display text-2xl lg:text-4xl font-extrabold tracking-tight text-amber-soft drop-shadow-[0_4px_24px_rgba(245,179,53,0.4)]">{venue.name}</div>
            )}
          </div>
          <div />
        </header>
      )}

      <main className="relative z-[1] flex flex-1 flex-col items-center justify-center px-8 pb-24 text-center">
        {phase === "question" && state.question && (
          <div className="w-full" data-testid="tv-question">
            <div className="mb-3 font-mono text-xs font-bold uppercase tracking-[0.4em] text-[#c9a3ff]">
              {CATEGORY_LABELS[state.game_category] || "General Knowledge"}
            </div>
            <div className="mb-6 inline-block rounded-full border border-amber/40 bg-amber/10 px-6 py-2 font-mono text-sm font-bold uppercase tracking-[0.4em] text-amber-soft">
              Question <span className="text-white">{state.q_index + 1}</span> / {state.question_total}
            </div>
            <h1 className="mx-auto mb-10 max-w-[86%] font-display text-[4.6vh] font-extrabold leading-[1.22]" data-testid="tv-question-text" style={{ textShadow: "0 4px 24px rgba(150,40,220,0.45)", textWrap: "balance" }}>
              {state.question.text}
            </h1>
            <div className="mx-auto grid max-w-[76vw] grid-cols-1 gap-4 sm:grid-cols-2 sm:max-w-[76vw]">
              {state.question.options.map((opt, i) => (
                <div key={i} data-testid={`tv-answer-${i}`} className="card-navy flex items-center gap-4 overflow-hidden rounded-3xl p-6 text-left font-display text-2xl font-bold" style={{ boxShadow: "0 4px 0 rgba(0,0,0,0.25), 0 6px 20px rgba(0,0,0,0.3)" }}>
                  <span className="font-mono-num flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#c44df0] to-[#8b2fd6] text-xl font-bold">{i + 1}</span>
                  {opt}
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-col items-center">
              <div className="h-3 w-[38vw] overflow-hidden rounded-full border border-ice/25 bg-navy-900/90">
                <div className="h-full" style={{ width: `${frac * 100}%`, background: "linear-gradient(90deg,#ff3d71,#ffb020,#ffd68a)", boxShadow: "0 0 12px rgba(255,180,60,0.6)", transition: "width 0.25s linear" }} />
              </div>
              <div className={`font-mono-num mt-3 text-4xl font-bold ${secondsLeft <= 3 ? "text-coral" : "text-amber-soft"}`} data-testid="tv-timer-num">{Math.ceil(secondsLeft)}</div>
              <div className="mt-3 flex items-center gap-3 rounded-full border border-amber/40 bg-navy-900/85 px-6 py-2">
                <span className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-mut">Points if correct now</span>
                <span className="font-mono-num text-2xl font-bold text-mint" data-testid="tv-points-value">{pointsIfCorrect(secondsLeft, qTime)}</span>
              </div>
            </div>
          </div>
        )}

        {phase === "reveal" && state.question && (
          <div className="w-full" data-testid="tv-reveal">
            <div className="answer-pop mx-auto mb-8 font-display text-[8.5vh] font-extrabold leading-none" data-testid="tv-answer-pop"
              style={{ backgroundImage: "linear-gradient(135deg,#34d8a8,#7ee89a)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", animation: "tk-answer-pop 0.9s cubic-bezier(0.34,1.56,0.64,1) both" }}
            >
              {state.question.options[state.question.correct]}
            </div>
            <style>{`@keyframes tk-answer-pop { 0% { transform: scale(0.3) rotate(-8deg); opacity: 0; filter: blur(8px);} 55% { transform: scale(1.15) rotate(2deg); opacity: 1; filter: blur(0);} 100% { transform: scale(1) rotate(0); opacity: 1;} }`}</style>
            <div className="mx-auto mb-6 grid max-w-[76vw] grid-cols-2 gap-4">
              {state.question.options.map((opt, i) => (
                <div key={i} className={`flex items-center gap-4 rounded-3xl border-2 p-6 text-left font-display text-2xl font-bold transition-all ${
                  i === state.question.correct ? "border-mint bg-gradient-to-br from-[#1a8e6a] to-[#0d4a37] text-white" : "border-ice/10 bg-navy-800/40 text-mut opacity-20"
                }`} data-testid={`tv-reveal-answer-${i}`}>
                  <span className={`font-mono-num flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-xl font-bold ${i === state.question.correct ? "bg-mint text-[#001a12]" : "bg-navy-700"}`}>{i + 1}</span>
                  {opt}
                </div>
              ))}
            </div>
            <div className="mx-auto max-w-2xl rounded-2xl border-l-4 border-amber-soft bg-navy-800/90 p-6 text-left" data-testid="tv-fact">
              <div className="mb-2 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-amber-soft">Did you know</div>
              <p className="text-xl leading-relaxed text-ice">{state.question.fact}</p>
            </div>
            <div className="mt-6 inline-block rounded-full border border-ice/25 bg-navy-900/85 px-6 py-2 font-mono text-xs font-bold uppercase tracking-[0.35em] text-[#c9a3ff]">
              Next question in {Math.ceil(secondsLeft)}
            </div>
          </div>
        )}

        {phase === "leaderboard" && (
          <div className="w-full" data-testid="tv-leaderboard">
            <h1 className="mb-2 font-display text-4xl font-extrabold uppercase tracking-[0.1em] text-amber text-glow-amber">Final standings</h1>
            {state.winner && (
              <div className="mb-6 flex items-center justify-center gap-4">
                <Avatar icon={state.winner.avatar_icon} color={state.winner.avatar_color} size={52} />
                <div className="text-left">
                  <div className="font-display text-2xl font-extrabold text-cream">{state.winner.name} takes the glory</div>
                  <div className="font-mono text-sm text-amber-soft">"{state.taunt}"</div>
                </div>
              </div>
            )}
            <div className="mx-auto max-w-3xl space-y-2">
              {state.standings.map((p, i) => (
                <div key={p.player_id} className={`grid grid-cols-[3.5rem_3rem_1fr_auto] items-center gap-4 rounded-2xl border px-5 py-3 text-xl ${i === 0 ? "border-amber-soft bg-gradient-to-r from-amber/30 to-amber/5" : "border-ice/20 bg-navy-800/70"}`} data-testid={`tv-lb-row-${i}`}>
                  <span className={`font-mono-num font-bold ${i === 0 ? "text-amber-soft" : "text-mut"}`}>{i + 1}</span>
                  <Avatar icon={p.avatar_icon} color={p.avatar_color} config={p.avatar_config} size={38} />
                  <span className="flex items-center gap-2 text-left font-display font-bold">
                    {p.name}
                    {p.streak >= 2 && (
                      <span className="flex items-center gap-1 rounded-full bg-coral/15 px-2 py-0.5 font-mono text-[10px] font-bold text-coral" data-testid={`tv-lb-streak-${i}`}>
                        <Flame size={11} />×{p.streak}
                      </span>
                    )}
                  </span>
                  <span className="font-mono-num font-bold text-mint">{p.score}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.35em] text-[#c9a3ff]">10-minute break starting…</p>
          </div>
        )}

        {phase === "break" && (
          <div className="relative -mx-8 -mb-24 flex min-h-0 w-[calc(100%+4rem)] flex-1 flex-col self-stretch" data-testid="tv-break">
            <BreakShow code={roomCode} venue={venue} winner={state.winner} taunt={state.taunt} winnerBadges={state.winner_badges} secondsLeft={secondsLeft} intermissionCode={state.intermission_code} checkedInCount={state.checked_in_count || 0} adsOnTv={state.ads_on_tv !== false} />
          </div>
        )}
      </main>

      {venue.specials && venue.specials.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 z-[5] overflow-hidden border-t-2 border-ice/15 bg-navy-900/90 py-3.5 backdrop-blur" data-testid="tv-specials-marquee">
          <div className="marquee-track items-center" style={{ animationDuration: `${venue.ticker_speed || 42}s` }}>
            {[0, 1].map((half) => (
              <div key={half} className="flex items-center gap-10 pr-10">
                <span className="flex items-center gap-3 whitespace-nowrap font-mono text-sm font-bold uppercase tracking-[0.3em] text-amber">
                  <UtensilsCrossed size={16} /> Tonight's specials
                  <span className="inline-block h-2.5 w-2.5 rotate-45 bg-amber/70" />
                </span>
                {venue.specials.map((s, i) => (
                  <span key={i} className="flex items-center gap-4 whitespace-nowrap">
                    {s.image_url && <img src={s.image_url} alt="" className="h-12 w-20 rounded-lg object-cover ring-1 ring-ice/25" />}
                    <span className="font-display text-2xl font-extrabold text-cream">{s.title}</span>
                    {s.description && <span className="text-base text-mut">{s.description}</span>}
                    <span className="inline-block h-2.5 w-2.5 rotate-45 bg-amber/70" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass fixed bottom-24 right-4 z-10 hidden items-center gap-4 rounded-2xl border-2 border-amber/50 p-4 sm:flex lg:right-8" data-testid="tv-join-card">
        <div className="rounded-xl bg-white p-2">
          <QRCodeSVG value={`${window.location.origin}/join?code=${roomCode}`} size={84} fgColor="#190a20" />
        </div>
        <div className="font-display text-lg font-extrabold uppercase leading-tight text-cream" data-testid="tv-join-caption">
          Scan to join
          <br />
          <span className="text-amber-soft">on your phone</span>
        </div>
      </div>
    </div>
  );
}
